import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthUtils } from './auth.utils';
import { OneIdClient, TokenModel } from 'src/NSwag/nswag-api-auth';
import { UserService } from '../user/user.service';
import { environment } from 'src/environment/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService
{
  private _authenticated: boolean = false;
  private _authenticated$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private oneidClient: OneIdClient,
    private userService: UserService
  )
  {
    this._authenticated$.next(!AuthUtils.isTokenExpired(this.accessToken));
    this._authenticated = !AuthUtils.isTokenExpired(this.accessToken);
  }

  set accessToken(token: string)
  {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string
  {
    return localStorage.getItem('accessToken') ?? '';
  }

  // get isAuthenticated(): boolean {
  //     return this._authenticated;
  // }

  get authenticated$(): Observable<boolean>
  {
    return this._authenticated$.asObservable();
  }

  signIn(code: string): Observable<TokenModel>
  {
    // Throw error, if the user is already logged in
    if (this._authenticated)
    {
      return throwError(() => new Error('User is already logged in.'));
    }

    return this.oneidClient.oneIdApi(code).pipe(
      map(tokenModel =>
      {
        if (!tokenModel?.token)
          throw new Error('Login failed. Applicant not registered in PiimaJob.');

        // Store the access token in the local storage
        this.accessToken = tokenModel.token;

        // Set the authenticated flag to true
        this._authenticated = true;
        this._authenticated$.next(true);
        // Return a new observable with the response
        return tokenModel;
      })
    );
  }

  /**
   * Sign out
   */
  signOut(): Observable<boolean>
  {
    localStorage.removeItem('accessToken');
    // this.userService.user = null;

    // Set the authenticated flag to false
    this._authenticated = false;
    this._authenticated$.next(false);

    // one id sign out
    this.oneidClient.singOut(this.userService.user?.id ?? '');

    // Return the observable
    return of(true);
  }



  /**
   * Sign in using the access token
   */
  private signInUsingToken(): Observable<boolean>
  {
    // Renew token
    return this.oneidClient.refreshAccessToken().pipe(
      switchMap((response: TokenModel) =>
      {
        // Store the access token in the local storage
        this.accessToken = response.token;

        // Set the authenticated flag to true
        this._authenticated = true;
        this._authenticated$.next(true);

        // Store the user on the user service
        // this.userService.user = response.user;

        // Return true
        return of(true);
      })
    );
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean>
  {
    // Check if the user is logged in
    if (this._authenticated)
    {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken)
    {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken))
    {
      return of(false);
    }

    // If the access token exists and it didn't expire, sign in using it
    return this.signInUsingToken();
  }

  // response_type: string | null = 'one_code';
  // client_id: string | null = 'vacancy_piima_uz';
  // redirect_uri: string | null = environment.base_url + '/#/oneid?localredirectUrl=';
  // scope: string | null = 'vacancy_piima_uz';
  // state: string | null = '{"method":"IDPW"}';

  goToOneIDPage(currentUrl: string): void
  {
    if (environment.response_type === undefined || environment.response_type === null)
      throw new Error("The parameter 'response_type' must be defined.");

    let redirect_uri = environment.redirect_uri += currentUrl;

    let url_ =
      'https://sso.egov.uz/sso/oauth/Authorization.do?' +
      'response_type={response_type}&client_id={client_id}' +
      '&redirect_uri={redirect_uri}&scope={scope}&state={state}';

    url_ = url_.replace(
      '{response_type}',
      encodeURIComponent(environment.response_type)
    );

    if (environment.client_id === undefined || environment.client_id === null)
      throw new Error("The parameter 'client_id' must be defined.");

    url_ = url_.replace('{client_id}', encodeURIComponent(environment.client_id));

    if (redirect_uri === undefined || redirect_uri === null)
      throw new Error("The parameter 'redirect_uri' must be defined.");

    url_ = url_.replace(
      '{redirect_uri}',
      encodeURIComponent('' + environment.redirect_uri)
    );

    if (environment.scope === undefined || environment.scope === null)
      throw new Error("The parameter 'scope' must be defined.");

    url_ = url_.replace('{scope}', encodeURIComponent('' + environment.scope));

    if (environment.state === undefined || environment.state === null)
      throw new Error("The parameter 'state' must be defined.");

    url_ = url_.replace('{state}', encodeURIComponent('' + environment.state));

    url_ = url_.replace(/[?&]$/, '');

    window.open(url_, '_self');
  }
}
